'use client';

import { Banknote, CreditCard } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import type { PaymentMethod } from '@/features/payments/domain/model/payment-method';
import { formatTotemPrice } from '@/features/totems/domain/service/totem-price';
import { cn } from '@/shared/utils/utils';

type Props = {
  methods: PaymentMethod[];
  onSelect: (method: PaymentMethod) => void;
  cartTotal: number;
  amountTendered: string;
  onAmountTenderedChange: (amount: string) => void;
  isPending?: boolean;
  isWebTotem?: boolean;
  /** Panel de caja: el QR del local se cobra contra la terminal, aunque el tótem sea web. */
  isDashboard?: boolean;
};

export function TotemPaymentMethodPicker({
  methods,
  onSelect,
  cartTotal,
  amountTendered,
  onAmountTenderedChange,
  isPending,
  isWebTotem,
  isDashboard,
}: Props) {
  const [pendingMethodId, setPendingMethodId] = useState<string | null>(null);

  if (methods.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 p-6 text-center text-muted-foreground">
        <p className="text-base font-semibold text-foreground">No hay métodos de pago disponibles</p>
        <p className="text-sm">El pago por Mercado Pago aún no se encuentra configurado para esta sucursal.</p>
      </div>
    );
  }

  const parsedAmount = parseFloat(amountTendered) || 0;
  const change = Math.max(0, parsedAmount - cartTotal);

  const cashMethod = methods.find(m => m.type === 'efectivo');
  const qrMethod = methods.find(m => m.type === 'qr');
  const mpLinkMethod = methods.find(m => m.type === 'mercado_pago');
  const cardMethod = methods.find(m => m.type === 'clover' || m.type === 'getnet' || m.type === 'epagos');

  // En el dashboard (panel de caja) el QR del local siempre se ofrece; en la carta/tótem web público
  // se cobra con la preferencia de Mercado Pago, no con el QR estático.
  const showQrSeparate = Boolean((isDashboard || !isWebTotem) && qrMethod);
  const showMpLink = mpLinkMethod || (isWebTotem && qrMethod);
  const actualMpMethod = mpLinkMethod || qrMethod;

  type PaymentOption = {
    method: PaymentMethod;
    label: string;
    description: string;
    icon: React.ReactNode;
    bg: string;
  };

  const paymentOptions: PaymentOption[] = [];
  if (cashMethod) {
    paymentOptions.push({
      method: cashMethod,
      label: 'Efectivo',
      description: 'Aboná en caja al retirar',
      icon: <Banknote className="size-6 text-emerald-600" />,
      bg: 'bg-emerald-100 dark:bg-emerald-950/40',
    });
  }
  if (showQrSeparate && qrMethod) {
    paymentOptions.push({
      method: qrMethod,
      label: 'Mercado Pago QR',
      description: 'Pagá escaneando el código QR',
      icon: <Image src="/mercado-pago.svg" alt="Mercado Pago" width={32} height={32} className="object-contain" />,
      bg: 'bg-[#009ee3]/10 dark:bg-[#009ee3]/20',
    });
  }
  // TEMPORAL: Comentado para Take Order (panel de caja). En carta web móvil (isWebTotem) se mantiene.
  // if (isWebTotem && showMpLink && actualMpMethod) {
  //   paymentOptions.push({
  //     method: actualMpMethod,
  //     label: 'Mercado Pago',
  //     description: 'Pagá con dinero en cuenta, débito o crédito',
  //     icon: <Image src="/mercado-pago.svg" alt="Mercado Pago" width={32} height={32} className="object-contain" />,
  //     bg: 'bg-[#009ee3]/10 dark:bg-[#009ee3]/20',
  //   });
  // }
  // TEMPORAL: Comentado para Take Order hasta estabilizar terminales POS.
  /*
  if (cardMethod) {
    paymentOptions.push({
      method: cardMethod,
      label: 'Tarjeta',
      description: 'Débito o Crédito en terminal',
      icon: <CreditCard className="size-6 text-cyan-600" />,
      bg: 'bg-cyan-100 dark:bg-cyan-950/40',
    });
  }
  */

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase">Método de pago</h3>
      <div className="flex flex-col gap-3">
        {paymentOptions.map(option => {
          const isThisPending = isPending && pendingMethodId === option.method.id;
          return (
            <button
              key={option.method.id}
              type="button"
              disabled={isPending}
              className={cn(
                'relative flex cursor-pointer items-center gap-4 rounded-2xl border border-border/70 p-4 text-left shadow-xs transition-all',
                !isPending && 'hover:border-primary/50 hover:bg-muted/30 active:scale-[0.99]',
                isPending && !isThisPending && 'cursor-not-allowed opacity-50',
                isThisPending && 'cursor-not-allowed border-primary opacity-100 ring-2 ring-primary'
              )}
              onClick={() => {
                setPendingMethodId(option.method.id);
                onSelect(option.method);
              }}
            >
              <div className={cn('flex size-12 shrink-0 items-center justify-center rounded-xl p-1.5', option.bg)}>
                {option.icon}
              </div>
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-lg font-bold text-foreground">{option.label}</span>
                {option.description && (
                  <span className="text-xs font-medium text-muted-foreground">{option.description}</span>
                )}
              </div>
              {isThisPending && <Spinner className="size-5 shrink-0 bg-primary" />}
            </button>
          );
        })}
      </div>

      {cashMethod && (
        <div className="mt-4 flex flex-col items-end gap-6 border-t pt-6 md:flex-row">
          <div className="flex-1 space-y-2">
            <label htmlFor="amount-tendered" className="text-xs font-medium text-muted-foreground">
              Calculadora de Vuelto (Opcional)
            </label>
            <div className="relative">
              <span className="absolute top-1/2 left-3 -translate-y-1/2 text-lg font-bold text-foreground">$</span>
              <Input
                id="amount-tendered"
                type="number"
                value={amountTendered}
                onChange={e => onAmountTenderedChange(e.target.value)}
                className="h-12 pl-8 text-xl font-bold shadow-none"
                placeholder="0"
              />
            </div>
          </div>
          <div className="flex-1 space-y-2">
            <span className="text-xs font-medium text-muted-foreground">Vuelto</span>
            <div className="flex h-12 items-center">
              <span className="text-xl font-bold text-emerald-600">{formatTotemPrice(change)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
